'use client';

import Link from 'next/link';
import { useState } from 'react';

interface City {
  name: string;
  slug: string;
  departmentCode: string;
  region: string;
}

interface RegionGroup {
  region: string;
  cities: City[];
}

export default function TAJCitiesList({ citiesByRegion }: { citiesByRegion: RegionGroup[] }) {
  const [openRegions, setOpenRegions] = useState<Set<string>>(new Set());

  const toggleRegion = (region: string) => {
    setOpenRegions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(region)) {
        newSet.delete(region);
      } else {
        newSet.add(region);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-3 md:space-y-4">
      {citiesByRegion.map(({ region, cities }) => (
        <div key={region} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => toggleRegion(region)}
            className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-gray-50 transition-colors"
            aria-expanded={openRegions.has(region)}
          >
            <h3 className="text-base md:text-lg font-bold text-gray-900">
              {region}
              <span className="text-xs md:text-sm font-normal text-gray-600 ml-2 md:ml-3">
                ({cities.length} {cities.length > 1 ? 'villes' : 'ville'})
              </span>
            </h3>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`w-5 h-5 text-primary transition-transform duration-300 flex-shrink-0 ${openRegions.has(region) ? 'rotate-180' : ''}`}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ${openRegions.has(region) ? 'max-h-[2000px]' : 'max-h-0'}`}
          >
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 p-4 md:p-5 pt-0 border-t border-gray-100">
              {cities.map((city) => (
                <li key={city.slug}>
                  <Link
                    href={`/services/taj/${city.slug}`}
                    className="block p-2 md:p-3 rounded-md hover:bg-secondary/10 transition-all group"
                  >
                    <span className="font-medium group-hover:text-primary transition-colors text-sm md:text-base">
                      {city.name}
                    </span>
                    <span className="text-xs text-gray-600 ml-1">({city.departmentCode})</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}
